import './Navbar.css';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { IoLogOutOutline } from 'react-icons/io5';
import { HiOutlineMap } from 'react-icons/hi';
import { HiOutlineAcademicCap } from 'react-icons/hi';
import { HiChevronDown } from 'react-icons/hi';
import {
  HiOutlineInformationCircle,
  HiOutlineUsers,
  HiOutlineBookmarkSquare,
  HiOutlineArrowDownTray,
  HiOutlineDocumentText,
  HiOutlineChartPie,
  HiOutlineHeart,
  HiOutlinePencilSquare,
  HiOutlineSquares2X2,
  HiOutlineMagnifyingGlass,
  HiOutlineUser,
  HiOutlineShieldCheck,
} from 'react-icons/hi2';
import { RiMenuUnfoldLine } from 'react-icons/ri';
import { useAuthStore } from '../../stores';
import { useRoles } from '../../hooks/useRoles';
import { formatRoleLabel } from '../../utils/roles';
import { logout as logoutUser } from '../../services/authService';
import { isTauri } from '../../utils/tauri';
import { isMacOS, hasDesktopBuild } from '../../utils/platform';
import DesktopDownloadModal from '../DesktopDownloadModal/DesktopDownloadModal';
import EditProfileDialog from '../EditProfileDialog/EditProfileDialog';
import ProfileMenu from './ProfileMenu';
import { environment } from '../../environment/environment';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const atlasLogo = require('../../assets/logos/atlas-lockup.png');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const atlasIcon = require('../../assets/logos/atlas-mark.png');

interface NavbarProps {
  onSidebarCollapse?: (collapsed: boolean) => void;
}

function Navbar({ onSidebarCollapse }: NavbarProps) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    dashboard: false,
    you: false,
    adminSettings: false,
  });
  const profileTriggerRef = useRef<HTMLDivElement>(null);
  const isDesktop = isTauri();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  // Get user name from query parameter, fallback to Zustand store
  const queryUserName = searchParams.get('name');
  const userName = queryUserName || user?.name || 'Admin';
  const { roles } = useRoles();
  const userRole = user?.role ? formatRoleLabel(roles.find((r) => r.name === user.role), user.role) : 'User';
  const userInitials = userName.slice(0, 2).toUpperCase();
  
  const isAdmin = user?.role === 'administrator';
  const canAccessDataMappings = isAdmin && (environment.environment === 'development' || environment.environment === 'local');

  // Determine active tab from current pathname
  const getActiveTab = (): 'opportunities-by-retailer' | 'opportunities-by-region' | 'exported-opportunities' | 'customer-health' | 'your-lists' | 'files' | 'user-list' | 'penetration-report' | 'penetration-report-v2' => {
    if (location.pathname.includes('/penetration-report-v2')) return 'penetration-report-v2';
    if (location.pathname.includes('/penetration-report')) return 'penetration-report';
    if (location.pathname.includes('/opportunitybymap')) return 'opportunities-by-region';
    if (location.pathname.includes('/exported-opportunities')) return 'exported-opportunities';
    if (location.pathname.includes('/customer-health')) return 'customer-health';
    if (location.pathname.includes('/your-lists') || location.pathname.includes('/list-stores')) return 'your-lists';
    if (location.pathname.includes('/files')) return 'files';
    if (location.pathname.includes('/user-list')) return 'user-list';
    return 'opportunities-by-retailer';
  };

  const currentTab = getActiveTab();

  // Only the group containing the active route stays expanded; all others collapse.
  useEffect(() => {
    if (currentTab === 'penetration-report' || currentTab === 'customer-health' || currentTab === 'penetration-report-v2') {
      setExpandedGroups({ dashboard: true, you: false, adminSettings: false });
    } else if (currentTab === 'your-lists' || currentTab === 'files') {
      setExpandedGroups({ dashboard: false, you: true, adminSettings: false });
    } else if (currentTab === 'user-list') {
      setExpandedGroups({ dashboard: false, you: false, adminSettings: true });
    }
  }, [currentTab]);

  const toggleGroup = (group: keyof typeof expandedGroups) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  // A group header is highlighted whenever one of its children is the active route.
  const isDashboardGroupActive = currentTab === 'penetration-report' || currentTab === 'customer-health' || currentTab === 'penetration-report-v2';
  const isYouGroupActive = currentTab === 'your-lists' || currentTab === 'files';
  const isAdminSettingsGroupActive = currentTab === 'user-list';

  const handleNavItemClick = (route: string) => {
    navigate(route);
    // Close sidebar on mobile after selecting
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleLogoClick = () => {
    navigate('/dashboard');
  };

  const handleLogout = async () => {
    // Call the comprehensive logout from authService
    // This clears localStorage, sessionStorage, IndexedDB, and auth state
    await logoutUser();

    // Clear Zustand store (this will attempt to write to localStorage)
    logout();

    // Clear all storage again AFTER Zustand logout to prevent it from saving state
    localStorage.clear();
    sessionStorage.clear();

    // Navigate to login
    navigate('/');
  };

  const handleSidebarToggle = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
      setSidebarCollapsed(false); // Ensure sidebar is expanded when opened on mobile
    } else {
      const newCollapsedState = !sidebarCollapsed;
      setSidebarCollapsed(newCollapsedState);
      if (onSidebarCollapse) {
        onSidebarCollapse(newCollapsedState);
      }
    }
  };

  return (
    <>
      {/* Mobile Top Header */}
      {isMobile && (
        <div className="mobile-top-header">
          <button className="mobile-hamburger-btn" onClick={() => setSidebarOpen(true)} title="Open Menu">
            <RiMenuUnfoldLine />
          </button>
          <div className="mobile-top-logo" onClick={handleLogoClick}>
            <img src={atlasLogo} alt="Atlas Logo" className="mobile-top-logo-img" />
          </div>
        </div>
      )}

      {/* Sidebar Overlay (mobile) */}
      {isMobile && sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'} ${sidebarCollapsed ? 'collapsed' : ''}`}>
        {/* Sidebar Header */}
        <div className="sidebar-nav-header">
          <div className="sidebar-logo" onClick={handleLogoClick}>
            <img
              src={sidebarCollapsed ? atlasIcon : atlasLogo}
              alt="Atlas Logo"
              className="logo-image"
            />
          </div>
          
          
          <button className={`sidebar-toggle-btn ${!sidebarCollapsed ? 'rotated' : ''}`} onClick={handleSidebarToggle} title="Toggle Sidebar">
            <RiMenuUnfoldLine />
          </button>
          
        </div>

        {/* Navigation Items */}
        <nav className="sidebar-nav">
          {/* Dashboard Group */}
          <div className="sidebar-nav-group">
            <button
              className={`sidebar-nav-group-header ${isDashboardGroupActive ? 'active' : ''}`}
              onClick={() => toggleGroup('dashboard')}
              aria-expanded={expandedGroups.dashboard}
              title="Dashboard"
            >
              <span className="nav-group-icon-badge">
                {/* @ts-ignore */}
                <HiOutlineSquares2X2 className="nav-item-icon" />
              </span>
              <span className="nav-group-label">Dashboard</span>
              {/* @ts-ignore */}
              <HiChevronDown className={`nav-group-chevron ${expandedGroups.dashboard ? 'expanded' : ''}`} />
            </button>

            <div className={`sidebar-nav-group-items ${expandedGroups.dashboard ? '' : 'collapsed-group'}`}>
              {(environment.environment === 'development' || environment.environment === 'local') && (
                <>
                <button
                  className={`sidebar-nav-subitem ${currentTab === 'penetration-report' ? 'active' : ''}`}
                  onClick={() => handleNavItemClick('/dashboard/penetration-report')}
                  title="Penetration Report"
                >
                  <span className="nav-subitem-bullet" />
                  {/* @ts-ignore */}
                  <HiOutlineChartPie className="nav-subitem-icon" />
                  <span className="nav-item-text">Penetration</span>
                </button>
                <button
                  className={`sidebar-nav-subitem ${currentTab === 'penetration-report-v2' ? 'active' : ''}`}
                  onClick={() => handleNavItemClick('/dashboard/penetration-report-v2')}
                  title="Penetration Report"
                >
                  <span className="nav-subitem-bullet" />
                  {/* @ts-ignore */}
                  <HiOutlineChartPie className="nav-subitem-icon" />
                  <span className="nav-item-text">Penetration V2</span>
                </button>
                </>
              )}

              {(environment.environment === 'development' || environment.environment === 'local') && (
                <button
                  className={`sidebar-nav-subitem ${currentTab === 'customer-health' ? 'active' : ''}`}
                  onClick={() => handleNavItemClick('/dashboard/customer-health')}
                  title="Customer Health"
                >
                  <span className="nav-subitem-bullet" />
                  {/* @ts-ignore */}
                  <HiOutlineHeart className="nav-subitem-icon" />
                  <span className="nav-item-text">Customer Health</span>
                </button>
              )}
            </div>
          </div>

          {/* Opportunity Finder */}
          <button
            className={`sidebar-nav-item ${currentTab === 'opportunities-by-retailer' ? 'active' : ''}`}
            onClick={() => handleNavItemClick('/dashboard/opportunity')}
            title="Opportunity Finder"
          >
            <span className="nav-standalone-icon-badge">
              {/* @ts-ignore */}
              <HiOutlineMagnifyingGlass className="nav-item-icon" />
            </span>
            <span className="nav-item-text">Opportunity Finder</span>
          </button>

          {/* Map */}
          <button
            className={`sidebar-nav-item ${currentTab === 'opportunities-by-region' ? 'active' : ''}`}
            onClick={() => handleNavItemClick('/dashboard/opportunitybymap')}
            title="Map"
          >
            <span className="nav-standalone-icon-badge">
              {/* @ts-ignore */}
              <HiOutlineMap className="nav-item-icon" />
            </span>
            <span className="nav-item-text">Map</span>
          </button>

          {/* You Group */}
          <div className="sidebar-nav-group">
            <button
              className={`sidebar-nav-group-header ${isYouGroupActive ? 'active' : ''}`}
              onClick={() => toggleGroup('you')}
              aria-expanded={expandedGroups.you}
              title="You"
            >
              <span className="nav-group-icon-badge">
                {/* @ts-ignore */}
                <HiOutlineUser className="nav-item-icon" />
              </span>
              <span className="nav-group-label">You</span>
              {/* @ts-ignore */}
              <HiChevronDown className={`nav-group-chevron ${expandedGroups.you ? 'expanded' : ''}`} />
            </button>

            <div className={`sidebar-nav-group-items ${expandedGroups.you ? '' : 'collapsed-group'}`}>
              <button
                className={`sidebar-nav-subitem ${currentTab === 'your-lists' ? 'active' : ''}`}
                onClick={() => handleNavItemClick('/dashboard/your-lists')}
                title="Lists"
              >
                <span className="nav-subitem-bullet" />
                {/* @ts-ignore */}
                <HiOutlineBookmarkSquare className="nav-subitem-icon" />
                <span className="nav-item-text">Lists</span>
              </button>

              <button
                className={`sidebar-nav-subitem ${currentTab === 'files' ? 'active' : ''}`}
                onClick={() => handleNavItemClick('/dashboard/files')}
                title="Files"
              >
                <span className="nav-subitem-bullet" />
                {/* @ts-ignore */}
                <HiOutlineDocumentText className="nav-subitem-icon" />
                <span className="nav-item-text">Files</span>
              </button>

              <a
                href="https://gatekeepersystemsinc-my.sharepoint.com/:f:/g/personal/jpande_gatekeepersystems_com/IgDLKv9C5YMaRbQiIHcd6GxnAUyM7V1HSrDlBqOJG7xuwaM?e=aPI2P8"
                target="_blank"
                rel="noopener noreferrer"
                className="sidebar-nav-subitem tutorial-link"
                title="Tutorial"
              >
                <span className="nav-subitem-bullet" />
                {/* @ts-ignore */}
                <HiOutlineAcademicCap className="nav-subitem-icon" />
                <span className="nav-item-text">Tutorial</span>
              </a>
            </div>
          </div>

          {/* Admin Settings Group */}
          <div className="sidebar-nav-group">
            <button
              className={`sidebar-nav-group-header ${isAdminSettingsGroupActive ? 'active' : ''}`}
              onClick={() => toggleGroup('adminSettings')}
              aria-expanded={expandedGroups.adminSettings}
              title="Admin Settings"
            >
              <span className="nav-group-icon-badge">
                {/* @ts-ignore */}
                <HiOutlineShieldCheck className="nav-item-icon" />
              </span>
              <span className="nav-group-label">Admin Settings</span>
              {/* @ts-ignore */}
              <HiChevronDown className={`nav-group-chevron ${expandedGroups.adminSettings ? 'expanded' : ''}`} />
            </button>

            <div className={`sidebar-nav-group-items ${expandedGroups.adminSettings ? '' : 'collapsed-group'}`}>
              <button
                className={`sidebar-nav-subitem ${currentTab === 'user-list' ? 'active' : ''}`}
                onClick={() => handleNavItemClick('/dashboard/user-list')}
                title="Members"
              >
                <span className="nav-subitem-bullet" />
                {/* @ts-ignore */}
                <HiOutlineUsers className="nav-subitem-icon" />
                <span className="nav-item-text">Members</span>
              </button>
            </div>
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          {/* Desktop App Banner — web only, and only where a desktop build
              exists (Windows + macOS); hidden on Linux and all phones/tablets. */}
          {!isDesktop && hasDesktopBuild() && (
            <button
              className="desktop-app-banner"
              onClick={() => setShowDownloadModal(true)}
              title="Get Atlas Desktop App"
            >
              {/* @ts-ignore */}
              <HiOutlineArrowDownTray className="desktop-app-banner-icon" />
              <div className="desktop-app-banner-text">
                <span className="desktop-app-banner-title">Get Desktop App</span>
                <span className="desktop-app-banner-sub">{isMacOS() ? 'macOS' : 'Windows'} · AI-powered</span>
              </div>
            </button>
          )}

          {/* Profile Section — for administrators, clicking the row (not the edit-pencil button)
              opens a popup menu with the Data Mappings admin area entry point. */}
          <div
            ref={profileTriggerRef}
            className="sidebar-profile-section"
            onClick={canAccessDataMappings ? () => setShowProfileMenu((prev) => !prev) : undefined}
            style={canAccessDataMappings ? { cursor: 'pointer' } : undefined}
          >
            <div className="profile-avatar">{userInitials}</div>
            <div className="profile-info">
              <span className="profile-name">{userName}</span>
              <span className="profile-role">{userRole}</span>
            </div>
            <button
              className="profile-edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowEditProfileModal((prev) => !prev);
              }}
              title="Edit Profile"
              aria-label="Edit Profile"
            >
              {/* @ts-ignore */}
              <HiOutlinePencilSquare className="profile-edit-icon" />
            </button>
          </div>

          <EditProfileDialog
            isOpen={showEditProfileModal}
            onClose={() => setShowEditProfileModal(false)}
            anchorRef={profileTriggerRef}
          />

          {canAccessDataMappings && (
            <ProfileMenu
              isOpen={showProfileMenu}
              onClose={() => setShowProfileMenu(false)}
              anchorRef={profileTriggerRef}
            />
          )}

          {/* Logout Button */}
          <button className="sidebar-footer-btn" onClick={handleLogout} title="Logout">
            {/* @ts-ignore */}
            <IoLogOutOutline className="footer-btn-icon" />
            <span>Logout</span>
          </button>
        </div>

        <div className="sidebar-version-info">
          <span className="version-date">Last updated: Jun 9, 2026</span>
          <div className="version-tooltip-wrapper">
            <HiOutlineInformationCircle className="version-info-icon" />
            <div className="version-tooltip">
              <div className="version-tooltip-title">What's New</div>
              <ul className="version-tooltip-list">
                <li>Rebranded application to Atlas</li>
                <li>Added store notes & comments</li>
                <li>Heatmap updates & improvements</li>
                <li>Usability changes for list & GK Account Penetration column</li>
                <li>New fields added to All Sites & filters</li>
                <li>Bug fixes & performance improvements</li>
              </ul>
            </div>
          </div>
        </div>
      </aside>

      {showDownloadModal && <DesktopDownloadModal onClose={() => setShowDownloadModal(false)} />}
    </>
  );
}

export default Navbar;
